// Procedural sound for Blast Radius, built only from Web Audio primitives:
// oscillators, noise buffers, gain envelopes, biquad filters and a small
// generated reverb. No audio files, no dependencies.
//
//   voice -> [pan] ------------------------------------------> master
//              \-> send -> highpass -> convolver -> return ---/
//   ambient drone (setTension) ------------------------------> master
//   master (volume, mute) -> compressor -> trim -> soft clip -> speakers
//
// Importing this module has no side effects beyond reading two localStorage
// keys. Without window/AudioContext (SSR, vitest in Node) every method is a
// silent no-op, and no AudioContext exists until unlock() runs in a gesture.

export type SfxName =
  | 'click' | 'place' | 'placeBelt' | 'remove' | 'rotate' | 'error'
  | 'release' | 'releaseBig' | 'flawed' | 'corrupt' | 'incident'
  | 'zap' | 'glitchSpawn' | 'glitchDie' | 'trip' | 'rollback' | 'patch'
  | 'research' | 'secret' | 'claim' | 'approval' | 'denied' | 'backfire' | 'protect'
  | 'nestDie' | 'drone' | 'win' | 'lose' | 'voiceOn' | 'voiceOff' | 'tick';

export interface PlayOpts {
  /** Semitone offset, default 0. Callers raise it for release streaks. */
  pitch?: number;
  /** 0..1 multiplier, default 1. */
  gain?: number;
  /** Stereo position, -1 (left) to 1 (right). */
  pan?: number;
}

interface Sfx {
  unlock(): void;
  play(name: SfxName, opts?: PlayOpts): void;
  setMuted(m: boolean): void;
  readonly muted: boolean;
  setVolume(v: number): void;
  readonly volume: number;
  setTension(t: number): void;
}

// ---------------------------------------------------------------- tuning

const MUTED_KEY = 'br.muted';
const VOLUME_KEY = 'br.volume';
const DEFAULT_VOLUME = 0.6;
/** Hard cap on overlapping sounds. Ordinary sounds stop at MAX - RESERVE so
 * important cues (win, incident, research...) always find a free slot. */
const MAX_VOICES = 24;
const PRIORITY_RESERVE = 4;
/** Schedule slightly ahead of the clock so attacks are never clipped. */
const LOOKAHEAD = 0.006;
/** The compressor adds automatic makeup gain; this trims quiet material back
 * to roughly unity so only loud stacks are affected. */
const MAKEUP_TRIM = 0.76;
/** The soft clipper is transparent below 0.7 and saturates smoothly toward
 * 0.98 for post-trim peaks up to this value; beyond it, output holds at 0.98. */
const CLIP_RANGE = 2;
const REVERB_RETURN = 0.5;

interface Spec {
  /** Minimum milliseconds between two plays of this sound. */
  gap: number;
  /** May use the reserved voices. */
  prio?: boolean;
  /** Random detune in cents so repeated sounds don't machine-gun. */
  vary?: number;
  /** Reverb send, 0..1. */
  send?: number;
  /** Mix trim in dB. This column is the whole balance of the game's sound. */
  db?: number;
}

// Trims were set by rendering every sound offline and measuring K-weighted
// loudness, so routine feedback sits well under alarms and wins.
const SPEC: Record<SfxName, Spec> = {
  click: { gap: 25, vary: 15, db: 5 },
  tick: { gap: 40, vary: 10, db: 4.5 },
  place: { gap: 30, vary: 25, send: 0.05, db: 2 },
  placeBelt: { gap: 22, vary: 40, db: 3 },
  remove: { gap: 30, vary: 25, send: 0.05, db: 4.5 },
  rotate: { gap: 40, vary: 20, db: 7.5 },
  error: { gap: 120, send: 0.05, db: 1.5 },
  release: { gap: 45, send: 0.28, db: 4.5 },
  releaseBig: { gap: 150, prio: true, send: 0.38, db: 4.5 },
  flawed: { gap: 90, send: 0.1, db: 4.5 },
  corrupt: { gap: 90, send: 0.08, db: 4 },
  incident: { gap: 900, prio: true, send: 0.18, db: 1.5 },
  zap: { gap: 70, vary: 60, send: 0.06, db: 3 },
  glitchSpawn: { gap: 120, vary: 40, send: 0.12, db: 3.5 },
  glitchDie: { gap: 50, vary: 50, send: 0.08, db: 5.5 },
  trip: { gap: 120, vary: 15, send: 0.1, db: 5.5 },
  rollback: { gap: 300, prio: true, send: 0.22, db: 2.5 },
  patch: { gap: 80, vary: 10, send: 0.15, db: 6 },
  research: { gap: 400, prio: true, send: 0.32, db: 4.5 },
  secret: { gap: 300, prio: true, send: 0.45, db: 8 },
  claim: { gap: 250, prio: true, send: 0.35, db: 5 },
  approval: { gap: 250, prio: true, send: 0.25, db: 4 },
  denied: { gap: 250, prio: true, send: 0.15, db: 3.5 },
  backfire: { gap: 200, prio: true, send: 0.15, db: 5 },
  protect: { gap: 150, send: 0.25, db: 3.5 },
  nestDie: { gap: 250, prio: true, send: 0.22, db: 4.5 },
  drone: { gap: 120, vary: 30, send: 0.08, db: 2.5 },
  win: { gap: 1500, prio: true, send: 0.4, db: 6.5 },
  lose: { gap: 1500, prio: true, send: 0.28, db: 3.5 },
  voiceOn: { gap: 80, prio: true, send: 0.1, db: 5.5 },
  voiceOff: { gap: 80, prio: true, send: 0.1, db: 5.5 },
};

// ---------------------------------------------------------------- engine

interface Drone {
  out: GainNode;
  filter: BiquadFilterNode;
  root: OscillatorNode;
  pair: readonly [OscillatorNode, OscillatorNode];
  edge: OscillatorNode;
  edgeGain: GainNode;
  lfo: OscillatorNode;
  lfoDepth: GainNode;
  /** Last tension pushed to the audio params, and when. */
  applied: number;
  appliedAt: number;
}

interface Engine {
  c: AudioContext;
  master: GainNode;
  /** Reverb send input. */
  verb: AudioNode;
  white: AudioBuffer;
  crush: AudioBuffer;
  nyq: number;
  drone: Drone;
}

interface Voice {
  e: Engine;
  /** Start time on the context clock. */
  t: number;
  /** Pitch ratio applied to every frequency in the recipe. */
  r: number;
  /** Per-sound gain; recipe layers connect here. */
  out: GainNode;
  /** Latest scheduled stop, used for the voice cap. */
  end: number;
  last: AudioScheduledSourceNode | null;
  /** Disconnected once the last source ends. */
  nodes: AudioNode[];
}

type AudioCtor = new (options?: AudioContextOptions) => AudioContext;

const hasWindow = typeof window !== 'undefined';

let muted = readStore(MUTED_KEY) === '1';
let volume = readVolume();
let tension = 0;
let eng: Engine | null = null;
/** Sounds may be scheduled briefly while a resume() from unlock() settles. */
let graceUntil = 0;
let voiceEnds: number[] = [];
const lastPlayed = new Map<SfxName, number>();

function audioCtor(): AudioCtor | null {
  if (!hasWindow) return null;
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function storage(): Storage | null {
  if (!hasWindow) return null;
  try {
    return window.localStorage ?? null;
  } catch {
    return null; // blocked storage (privacy mode, sandboxed iframe)
  }
}

function readStore(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStore(key: string, value: string): void {
  try {
    storage()?.setItem(key, value);
  } catch {
    // Quota or privacy errors: the setting still applies for this session.
  }
}

function readVolume(): number {
  const raw = readStore(VOLUME_KEY);
  const v = raw === null ? NaN : Number.parseFloat(raw);
  return Number.isFinite(v) ? clamp(v, 0, 1) : DEFAULT_VOLUME;
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

function num(x: unknown, fallback: number): number {
  return typeof x === 'number' && Number.isFinite(x) ? x : fallback;
}

/** Squared taper so the slider feels even: 0.5 is about half as loud. */
function masterLevel(): number {
  return muted ? 0 : volume * volume;
}

function build(Ctor: AudioCtor): Engine | null {
  let c: AudioContext;
  try {
    c = new Ctor({ latencyHint: 'interactive' });
  } catch {
    try {
      c = new Ctor();
    } catch {
      return null;
    }
  }

  const master = c.createGain();
  // Full level at once, so a sound played in the unlocking gesture is heard.
  // Nothing else is playing yet; the drone fades itself in.
  master.gain.value = masterLevel();
  const comp = c.createDynamicsCompressor();
  comp.threshold.value = -10;
  comp.knee.value = 8;
  comp.ratio.value = 3;
  comp.attack.value = 0.003;
  comp.release.value = 0.25;
  const trim = c.createGain();
  trim.gain.value = MAKEUP_TRIM / CLIP_RANGE;
  const clip = c.createWaveShaper();
  clip.curve = softClipCurve();
  master.connect(comp);
  comp.connect(trim);
  trim.connect(clip);
  clip.connect(c.destination);

  // A short, dark room so chimes have somewhere to ring. Lows stay dry.
  const verbIn = c.createBiquadFilter();
  verbIn.type = 'highpass';
  verbIn.frequency.value = 280;
  verbIn.Q.value = 0;
  const conv = c.createConvolver();
  conv.buffer = impulse(c, 1.4);
  const ret = c.createGain();
  ret.gain.value = REVERB_RETURN;
  verbIn.connect(conv);
  conv.connect(ret);
  ret.connect(master);

  const e: Engine = {
    c,
    master,
    verb: verbIn,
    white: whiteNoise(c, 1),
    crush: crushedNoise(c, 0.6),
    nyq: c.sampleRate / 2,
    drone: makeDrone(c, master, tension),
  };
  applyTension(e, tension, 1.5); // fades the drone in from silence
  return e;
}

/** Identity below 0.7, then a tanh knee that never exceeds 0.98. The
 * WaveShaper maps input [-1, 1] onto the curve, so the curve is stretched by
 * CLIP_RANGE and the trim before it divides by the same amount. */
function softClipCurve(): Float32Array<ArrayBuffer> {
  const n = 2049; // odd, so 0 maps to 0 exactly
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = ((i / (n - 1)) * 2 - 1) * CLIP_RANGE;
    const a = Math.abs(x);
    const y = a <= 0.7 ? a : 0.7 + 0.28 * Math.tanh((a - 0.7) / 0.28);
    curve[i] = Math.sign(x) * y;
  }
  return curve;
}

function whiteNoise(c: BaseAudioContext, seconds: number): AudioBuffer {
  const b = c.createBuffer(1, Math.floor(c.sampleRate * seconds), c.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
}

/** Sample-and-hold noise at a wandering low rate, quantized to seven levels:
 * the crunchy, bit-crushed texture used for corruption and debris. */
function crushedNoise(c: BaseAudioContext, seconds: number): AudioBuffer {
  const b = c.createBuffer(1, Math.floor(c.sampleRate * seconds), c.sampleRate);
  const d = b.getChannelData(0);
  let held = 0;
  let left = 0;
  for (let i = 0; i < d.length; i++) {
    if (left-- <= 0) {
      left = 3 + Math.floor(Math.random() ** 2 * 48);
      held = Math.round((Math.random() * 2 - 1) * 3) / 3;
    }
    d[i] = held * 0.8;
  }
  return b;
}

/** Stereo decaying noise that darkens as it fades, after a 12 ms pre-delay. */
function impulse(c: BaseAudioContext, seconds: number): AudioBuffer {
  const rate = c.sampleRate;
  const len = Math.floor(rate * seconds);
  const pre = Math.floor(rate * 0.012);
  const b = c.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const d = b.getChannelData(ch);
    let lp = 0;
    for (let i = pre; i < len; i++) {
      const x = (i - pre) / (len - pre);
      lp += (0.62 - 0.5 * x) * (Math.random() * 2 - 1 - lp);
      d[i] = lp * (1 - x) ** 3.5;
    }
  }
  return b;
}

// ---------------------------------------------------------------- drone

interface DroneParams {
  f0: number;
  spread: number;
  edge: number;
  cutoff: number;
  q: number;
  level: number;
  depth: number;
  rate: number;
}

/** Tension 0 is a barely-there hum; 1 is a brighter, pulsing drone a minor
 * sixth higher, with faster beating and a tritone edge fading in. */
function droneParams(t: number): DroneParams {
  const level = 0.004 + 0.075 * t ** 1.4;
  return {
    f0: 55 * 2 ** ((8 * t) / 12),
    spread: 3 + 14 * t, // cents of detune between the saw pair
    edge: 0.3 * Math.max(0, (t - 0.45) / 0.55) ** 2,
    cutoff: 160 * 2 ** (3.3 * t),
    q: 4 * t, // dB of resonance (lowpass Q is in dB in Web Audio)
    level,
    depth: level * 0.3, // tremolo depth
    rate: 0.1 + 1.6 * t * t, // tremolo rate, Hz
  };
}

function makeDrone(c: AudioContext, dest: AudioNode, t: number): Drone {
  const p = droneParams(t);
  const out = c.createGain();
  out.gain.value = 0;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = p.cutoff;
  filter.Q.value = p.q;
  filter.connect(out);
  out.connect(dest);

  const layer = (type: OscillatorType, freq: number, level: number) => {
    const o = c.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = c.createGain();
    g.gain.value = level;
    o.connect(g);
    g.connect(filter);
    o.start();
    return [o, g] as const;
  };
  const [root] = layer('sine', p.f0, 0.55);
  const [a] = layer('sawtooth', p.f0 * 2, 0.18);
  const [b] = layer('sawtooth', p.f0 * 2, 0.18);
  a.detune.value = -p.spread;
  b.detune.value = p.spread;
  const [edge, edgeGain] = layer('triangle', p.f0 * 2 * Math.SQRT2, 0);

  const lfo = c.createOscillator();
  lfo.frequency.value = p.rate;
  const lfoDepth = c.createGain();
  lfoDepth.gain.value = 0;
  lfo.connect(lfoDepth);
  lfoDepth.connect(out.gain);
  lfo.start();

  return { out, filter, root, pair: [a, b], edge, edgeGain, lfo, lfoDepth, applied: t, appliedAt: 0 };
}

/** setTargetAtTime glides from wherever each param currently is: no clicks. */
function applyTension(e: Engine, t: number, tc: number): void {
  const d = e.drone;
  const p = droneParams(t);
  const now = e.c.currentTime;
  const to = (param: AudioParam, value: number) => param.setTargetAtTime(value, now, tc);
  to(d.root.frequency, p.f0);
  to(d.pair[0].frequency, p.f0 * 2);
  to(d.pair[1].frequency, p.f0 * 2);
  to(d.pair[0].detune, -p.spread);
  to(d.pair[1].detune, p.spread);
  to(d.edge.frequency, p.f0 * 2 * Math.SQRT2);
  to(d.edgeGain.gain, p.edge);
  to(d.filter.frequency, p.cutoff);
  to(d.filter.Q, p.q);
  to(d.out.gain, p.level);
  to(d.lfoDepth.gain, p.depth);
  to(d.lfo.frequency, p.rate);
  d.applied = t;
  d.appliedAt = now;
}

// ---------------------------------------------------------------- building blocks

function voice(e: Engine, t: number, r: number, gain: number, pan: number, send: number): Voice {
  const c = e.c;
  const out = c.createGain();
  out.gain.value = gain;
  const nodes: AudioNode[] = [out];
  let head: AudioNode = out;
  if (pan !== 0 && typeof c.createStereoPanner === 'function') {
    const p = c.createStereoPanner();
    p.pan.value = pan;
    out.connect(p);
    head = p;
    nodes.push(p);
  }
  head.connect(e.master);
  if (send > 0) {
    const s = c.createGain();
    s.gain.value = send;
    head.connect(s);
    s.connect(e.verb);
    nodes.push(s);
  }
  return { e, t, r, out, end: t, last: null, nodes };
}

function track(v: Voice, src: AudioScheduledSourceNode, stop: number): void {
  if (stop >= v.end) {
    v.end = stop;
    v.last = src;
  }
}

function finish(v: Voice): void {
  const release = () => {
    for (const n of v.nodes) {
      try {
        n.disconnect();
      } catch {
        // already disconnected
      }
    }
  };
  if (v.last) v.last.onended = release;
  else release();
}

/** Attack to peak, optional hold, then an exponential fall to silence. */
function envelope(c: BaseAudioContext, t: number, a: number, hold: number, d: number, peak: number): GainNode {
  const g = c.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(peak, t + a);
  if (hold > 0) g.gain.setValueAtTime(peak, t + a + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, t + a + hold + d);
  return g;
}

function band(v: Voice, hz: number): number {
  return Math.min(Math.max(hz, 20), v.e.nyq * 0.95);
}

/** Pitch-tracking filter. For lowpass/highpass, Q is resonance in dB. */
function filter(
  v: Voice, input: AudioNode, type: BiquadFilterType, hz: number, q: number,
  t: number, to?: number, len = 0,
): BiquadFilterNode {
  const f = v.e.c.createBiquadFilter();
  f.type = type;
  f.Q.value = q;
  f.frequency.setValueAtTime(band(v, hz * v.r), t);
  if (to !== undefined) f.frequency.exponentialRampToValueAtTime(band(v, to * v.r), t + len);
  input.connect(f);
  return f;
}

function modulate(
  v: Voice, param: AudioParam, rate: number, depth: number, type: OscillatorType, t: number, stop: number,
): void {
  const m = v.e.c.createOscillator();
  m.type = type;
  m.frequency.value = rate;
  const g = v.e.c.createGain();
  g.gain.value = depth;
  m.connect(g);
  g.connect(param);
  m.start(t);
  m.stop(stop);
}

interface ToneOpts {
  /** Frequency in Hz before the pitch offset. */
  f: number;
  /** Exponential glide target, Hz. */
  to?: number;
  /** Glide time in seconds; defaults to the whole note. */
  glide?: number;
  /** Instant frequency jumps: [seconds after start, Hz]. */
  steps?: ReadonlyArray<readonly [number, number]>;
  type?: OscillatorType;
  at?: number;
  a?: number;
  hold?: number;
  d: number;
  peak: number;
  detune?: number;
  lp?: number;
  lpTo?: number;
  hp?: number;
  q?: number;
  /** Frequency modulation: [rate Hz, depth Hz, waveform]. */
  fm?: readonly [number, number, OscillatorType?];
  /** Vibrato: [rate Hz, depth cents]. */
  vib?: readonly [number, number];
  out?: AudioNode;
}

function tone(v: Voice, o: ToneOpts): void {
  const { c, nyq } = v.e;
  const f = o.f * v.r;
  // Partials pushed past the audible band by a big transpose are dropped.
  if (Math.max(f, (o.to ?? 0) * v.r) > nyq * 0.9) return;
  const t = v.t + (o.at ?? 0);
  const a = o.a ?? 0.003;
  const hold = o.hold ?? 0;
  const len = a + hold + o.d;
  const osc = c.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(f, t);
  if (o.to !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.to * v.r), t + (o.glide ?? len));
  }
  if (o.steps) for (const [dt, hz] of o.steps) osc.frequency.setValueAtTime(hz * v.r, t + dt);
  if (o.detune) osc.detune.value = o.detune;
  let head: AudioNode = osc;
  if (o.hp) head = filter(v, head, 'highpass', o.hp, o.q ?? 0, t);
  if (o.lp) head = filter(v, head, 'lowpass', o.lp, o.q ?? 0, t, o.lpTo, len);
  const g = envelope(c, t, a, hold, o.d, o.peak);
  head.connect(g);
  g.connect(o.out ?? v.out);
  const stop = t + len + 0.03;
  if (o.fm) modulate(v, osc.frequency, o.fm[0], o.fm[1] * v.r, o.fm[2] ?? 'sine', t, stop);
  if (o.vib) modulate(v, osc.detune, o.vib[0], o.vib[1], 'sine', t, stop);
  osc.start(t);
  osc.stop(stop);
  track(v, osc, stop);
}

interface NoiseOpts {
  /** Filter frequency in Hz before the pitch offset. */
  f: number;
  /** Filter sweep target, Hz. */
  to?: number;
  /** Linear Q for bandpass; dB of resonance for lowpass/highpass. */
  q?: number;
  type?: BiquadFilterType;
  at?: number;
  a?: number;
  hold?: number;
  d: number;
  peak: number;
  /** Use the bit-crushed buffer instead of white noise. */
  crush?: boolean;
  rate?: number;
  out?: AudioNode;
}

function noise(v: Voice, o: NoiseOpts): void {
  const { c } = v.e;
  const t = v.t + (o.at ?? 0);
  const a = o.a ?? 0.002;
  const hold = o.hold ?? 0;
  const len = a + hold + o.d;
  const buf = o.crush ? v.e.crush : v.e.white;
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  // Crushed noise has a pitch-like grain, so it follows the transpose too.
  src.playbackRate.value = (o.rate ?? 1) * (o.crush ? v.r : 1);
  const f = filter(v, src, o.type ?? 'bandpass', o.f, o.q ?? 1, t, o.to, len);
  const g = envelope(c, t, a, hold, o.d, o.peak);
  f.connect(g);
  g.connect(o.out ?? v.out);
  const stop = t + len + 0.03;
  src.start(t, Math.random() * buf.duration * 0.9);
  src.stop(stop);
  track(v, src, stop);
}

function mtof(m: number): number {
  return 440 * 2 ** ((m - 69) / 12);
}

/** Glassy struck-bar tone: the 1 : 2.76 : 5.40 modes of a glockenspiel bar. */
function glock(v: Voice, m: number, at: number, peak: number, d: number, out?: AudioNode): void {
  const f = mtof(m);
  tone(v, { f, at, a: 0.002, d, peak, out });
  tone(v, { f: f * 2.756, at, a: 0.001, d: d * 0.4, peak: peak * 0.3, out });
  tone(v, { f: f * 5.404, at, a: 0.001, d: d * 0.18, peak: peak * 0.1, out });
}

/** Soft chorused triangle note for arpeggios and pads. */
function warm(v: Voice, m: number, at: number, peak: number, d: number, hold = 0, a = 0.006): void {
  const f = mtof(m);
  for (const detune of [-6, 6]) {
    tone(v, { type: 'triangle', f, at, a, hold, d, peak: peak * 0.6, detune, lp: 2000 });
  }
  tone(v, { f: f * 2, at, a, hold, d: d * 0.5, peak: peak * 0.15 });
}

function panTo(v: Voice, x: number): AudioNode {
  const c = v.e.c;
  if (typeof c.createStereoPanner !== 'function') return v.out;
  const p = c.createStereoPanner();
  p.pan.value = x;
  p.connect(v.out);
  v.nodes.push(p);
  return p;
}

const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);

// ---------------------------------------------------------------- recipes

const RECIPES: Record<SfxName, (v: Voice) => void> = {
  click(v) {
    tone(v, { type: 'triangle', f: 1900, to: 1250, glide: 0.015, a: 0.001, d: 0.035, peak: 0.09 });
    noise(v, { f: 4200, q: 1.2, a: 0.0005, d: 0.008, peak: 0.04 });
  },

  tick(v) {
    tone(v, { f: 2600, a: 0.001, d: 0.022, peak: 0.05 });
    noise(v, { type: 'highpass', f: 6000, q: 0, a: 0.0005, d: 0.006, peak: 0.015 });
  },

  place(v) {
    tone(v, { f: 190, to: 75, glide: 0.09, a: 0.002, d: 0.14, peak: 0.22 }); // thump
    noise(v, { f: 1400, q: 1.1, a: 0.001, d: 0.035, peak: 0.13 }); // clack
    tone(v, { type: 'triangle', f: 740, at: 0.028, a: 0.001, d: 0.045, peak: 0.06, lp: 3000 }); // latch
  },

  placeBelt(v) {
    tone(v, { f: 300, to: 150, glide: 0.04, a: 0.001, d: 0.06, peak: 0.11 });
    noise(v, { f: 2800, q: 1.4, a: 0.0005, d: 0.016, peak: 0.045 });
  },

  remove(v) {
    tone(v, { f: 480, to: 150, glide: 0.09, a: 0.002, d: 0.11, peak: 0.12 });
    noise(v, { type: 'lowpass', f: 3500, to: 600, q: 0, a: 0.004, d: 0.1, peak: 0.05 });
  },

  rotate(v) {
    noise(v, { f: 3200, q: 2, a: 0.0005, d: 0.012, peak: 0.07 });
    tone(v, { type: 'triangle', f: 880, a: 0.001, d: 0.03, peak: 0.04 });
    noise(v, { f: 3600, q: 2, at: 0.04, a: 0.0005, d: 0.012, peak: 0.06 });
    tone(v, { type: 'triangle', f: 1175, at: 0.04, a: 0.001, d: 0.035, peak: 0.04 });
  },

  error(v) {
    for (const detune of [-12, 12]) {
      tone(v, { type: 'square', f: 155, to: 140, a: 0.004, hold: 0.06, d: 0.1, peak: 0.06, detune, lp: 950 });
    }
  },

  release(v) {
    glock(v, 72, 0, 0.13, 0.34); // C5
    glock(v, 79, 0.065, 0.15, 0.45); // G5
  },

  releaseBig(v) {
    [72, 76, 79, 84].forEach((m, i) => glock(v, m, i * 0.045, 0.1, 0.7));
    warm(v, 60, 0, 0.09, 0.6, 0.12, 0.01);
    warm(v, 67, 0.02, 0.06, 0.55, 0.1, 0.01);
    tone(v, { f: mtof(96), at: 0.16, a: 0.04, d: 0.5, peak: 0.025, vib: [6, 12] });
  },

  flawed(v) {
    const buzz = {
      type: 'sawtooth', f: 330, to: 196, glide: 0.28, a: 0.008, hold: 0.06, d: 0.24, lp: 1200, lpTo: 600, q: 2,
    } as const;
    tone(v, { ...buzz, peak: 0.09 });
    tone(v, { ...buzz, peak: 0.06, detune: 14 });
    tone(v, { f: 110, a: 0.004, d: 0.14, peak: 0.06 });
  },

  corrupt(v) {
    const bursts = [[0, 0.045], [0.07, 0.03], [0.115, 0.075]] as const;
    for (const [at, hold] of bursts) {
      noise(v, { crush: true, at, a: 0.002, hold, d: 0.025, peak: 0.13, f: 1700, q: 0.7, rate: rand(0.75, 1.25) });
    }
    const pick = () => [150, 200, 260, 310, 410][Math.floor(Math.random() * 5)];
    const steps = [[0.045, pick()], [0.09, pick()], [0.135, pick()]] as const;
    tone(v, { type: 'square', f: pick(), a: 0.002, hold: 0.17, d: 0.05, peak: 0.04, lp: 1500, steps });
  },

  incident(v) {
    const hi = 784; // G5
    const lo = 587; // D5
    const seg = 0.13;
    const steps: Array<[number, number]> = [];
    for (let i = 1; i < 6; i++) steps.push([i * seg, i % 2 ? lo : hi]);
    const hold = 6 * seg - 0.012;
    tone(v, { type: 'triangle', f: hi, steps, a: 0.012, hold, d: 0.12, peak: 0.12 });
    tone(v, { type: 'square', f: hi, steps, a: 0.012, hold, d: 0.12, peak: 0.03, lp: 2400 });
  },

  zap(v) {
    tone(v, { f: 2800, to: 1100, glide: 0.04, a: 0.001, d: 0.06, peak: 0.06 });
    tone(v, { type: 'triangle', f: 5600, to: 2200, glide: 0.04, a: 0.001, d: 0.03, peak: 0.015 });
    noise(v, { type: 'highpass', f: 5000, q: 0, a: 0.0005, d: 0.01, peak: 0.02 });
  },

  glitchSpawn(v) {
    tone(v, { type: 'triangle', f: 330, to: 480, a: 0.02, d: 0.18, peak: 0.07, lp: 2400, fm: [31, 70, 'square'] });
    noise(v, { crush: true, f: 3000, q: 2, a: 0.005, d: 0.08, peak: 0.03 });
  },

  glitchDie(v) {
    tone(v, { type: 'square', f: 1100, to: 180, glide: 0.07, a: 0.001, d: 0.09, peak: 0.045, lp: 3000 });
    noise(v, { crush: true, f: 2400, q: 1, a: 0.001, d: 0.05, peak: 0.07 });
    tone(v, { f: 700, to: 300, glide: 0.05, a: 0.001, d: 0.05, peak: 0.05 });
  },

  trip(v) {
    noise(v, { f: 2200, q: 1.5, a: 0.001, d: 0.03, peak: 0.17 }); // relay clack
    tone(v, { f: 120, to: 60, glide: 0.1, a: 0.002, d: 0.15, peak: 0.14 });
    for (const detune of [0, 25]) {
      tone(v, { type: 'sawtooth', f: 100, at: 0.012, a: 0.004, hold: 0.06, d: 0.08, peak: 0.04, lp: 700, detune });
    }
  },

  rollback(v) {
    tone(v, { f: 180, to: 1400, glide: 0.42, a: 0.03, hold: 0.32, d: 0.12, peak: 0.1 });
    tone(v, { type: 'triangle', f: 360, to: 2800, glide: 0.42, a: 0.03, hold: 0.32, d: 0.1, peak: 0.035, lp: 4000 });
    noise(v, { f: 400, to: 4000, q: 1.6, a: 0.36, d: 0.08, peak: 0.06 });
    tone(v, { f: mtof(91), at: 0.44, a: 0.002, d: 0.16, peak: 0.07 }); // lands on G6
  },

  patch(v) {
    noise(v, { f: 3000, q: 2, a: 0.0005, d: 0.012, peak: 0.06 });
    noise(v, { f: 3400, q: 2, at: 0.045, a: 0.0005, d: 0.012, peak: 0.055 });
    tone(v, { f: mtof(76), at: 0.09, a: 0.004, d: 0.26, peak: 0.06 });
    tone(v, { f: mtof(83), at: 0.09, a: 0.004, d: 0.26, peak: 0.05 });
  },

  research(v) {
    [60, 64, 67, 71, 74].forEach((m, i) => warm(v, m, i * 0.075, 0.1, 0.55)); // Cmaj9
    tone(v, { f: mtof(48), a: 0.02, hold: 0.2, d: 0.5, peak: 0.05 });
  },

  secret(v) {
    [88, 91, 93, 96, 98, 100, 103].forEach((m, i) => {
      glock(v, m, i * 0.045 + rand(0, 0.012), 0.055 - i * 0.004, 0.3, panTo(v, rand(-0.6, 0.6)));
    });
    noise(v, { type: 'highpass', f: 5000, q: 0, a: 0.15, d: 0.25, peak: 0.02 });
  },

  claim(v) {
    tone(v, { f: 140, to: 60, glide: 0.08, a: 0.002, d: 0.12, peak: 0.16 });
    [84, 88, 91, 96].forEach((m, i) => glock(v, m, 0.04 + i * 0.05, 0.065, 0.32));
  },

  approval(v) {
    tone(v, { f: 160, to: 70, glide: 0.07, a: 0.002, d: 0.1, peak: 0.13 }); // stamp
    noise(v, { f: 1800, q: 1, a: 0.001, d: 0.02, peak: 0.05 });
    glock(v, 84, 0.04, 0.09, 0.6); // C6 + E6
    glock(v, 88, 0.04, 0.08, 0.6);
    glock(v, 91, 0.14, 0.07, 0.5); // lifts to G6
  },

  denied(v) {
    tone(v, { f: 120, to: 55, glide: 0.08, a: 0.002, d: 0.12, peak: 0.13 }); // stamp
    tone(v, { type: 'square', f: mtof(67), at: 0.03, a: 0.004, hold: 0.06, d: 0.1, peak: 0.05, lp: 1200 });
    tone(v, { type: 'square', f: mtof(64), at: 0.15, a: 0.004, hold: 0.08, d: 0.16, peak: 0.05, lp: 1100 });
  },

  backfire(v) {
    noise(v, { crush: true, type: 'lowpass', f: 2600, to: 400, q: 0, a: 0.002, d: 0.26, peak: 0.15 });
    tone(v, { type: 'sawtooth', f: 260, to: 70, glide: 0.3, a: 0.004, d: 0.32, peak: 0.12, lp: 900 });
    tone(v, { f: 90, a: 0.002, d: 0.12, peak: 0.07 });
  },

  protect(v) {
    for (const [f, peak] of [[392, 0.06], [587, 0.045]] as const) {
      tone(v, { f: f * 0.92, to: f, glide: 0.06, a: 0.02, hold: 0.08, d: 0.3, peak, fm: [24, 10] });
    }
    noise(v, { f: 3000, q: 3, a: 0.03, d: 0.2, peak: 0.03 });
  },

  nestDie(v) {
    tone(v, { f: 110, to: 38, glide: 0.5, a: 0.003, d: 0.6, peak: 0.2 }); // boom
    noise(v, { type: 'lowpass', f: 2600, to: 180, q: 0, a: 0.004, d: 0.7, peak: 0.2 });
    noise(v, { crush: true, f: 1500, q: 0.8, at: 0.05, a: 0.002, hold: 0.1, d: 0.25, peak: 0.07 }); // debris
    tone(v, { type: 'square', f: 520, to: 60, glide: 0.45, a: 0.004, d: 0.45, peak: 0.04, lp: 1200 });
  },

  drone(v) {
    tone(v, {
      type: 'sawtooth', f: 380, to: 620, glide: 0.12, a: 0.01, hold: 0.05, d: 0.12, peak: 0.045,
      lp: 1600, fm: [48, 25, 'square'],
    });
    tone(v, { f: 1760, at: 0.13, a: 0.002, d: 0.05, peak: 0.025 });
  },

  win(v) {
    [60, 64, 67, 72, 76, 79].forEach((m, i) => warm(v, m, i * 0.085, 0.09, 0.5));
    const at = 0.52;
    for (const m of [60, 67, 72, 76]) warm(v, m, at, 0.06, 1.2, 0.5, 0.12); // held C chord
    tone(v, { f: mtof(84), at, a: 0.12, hold: 0.5, d: 1.0, peak: 0.02, vib: [5, 8] });
    glock(v, 84, at, 0.06, 0.8);
    glock(v, 91, at + 0.1, 0.05, 0.9);
  },

  lose(v) {
    const fall = {
      type: 'sawtooth', f: 196, to: 98, glide: 1.1, a: 0.03, hold: 0.35, d: 0.9, lp: 1400, lpTo: 300, q: 0,
    } as const;
    tone(v, { ...fall, peak: 0.07, detune: -8 });
    tone(v, { ...fall, peak: 0.07, detune: 8 });
    tone(v, { f: 98, to: 49, glide: 1.1, a: 0.03, hold: 0.35, d: 0.9, peak: 0.12 });
  },

  voiceOn(v) {
    tone(v, { f: 880, a: 0.004, d: 0.12, peak: 0.07 });
    tone(v, { f: 1318.5, at: 0.06, a: 0.004, d: 0.16, peak: 0.07 });
  },

  voiceOff(v) {
    tone(v, { f: 1318.5, a: 0.004, d: 0.12, peak: 0.06 });
    tone(v, { f: 880, at: 0.06, a: 0.004, d: 0.16, peak: 0.06 });
  },
};

// ---------------------------------------------------------------- public API

function unlock(): void {
  const Ctor = audioCtor();
  if (!Ctor) return;
  try {
    if (!eng) eng = build(Ctor);
    if (!eng) return;
    const c = eng.c;
    if (c.state !== 'running') {
      graceUntil = Date.now() + 300;
      void c.resume().catch(() => undefined);
      // Older iOS only opens the output once a buffer starts inside the gesture.
      const s = c.createBufferSource();
      s.buffer = c.createBuffer(1, 1, c.sampleRate);
      s.connect(c.destination);
      s.start(0);
    }
  } catch {
    // Audio stays off; the game carries on.
  }
}

function play(name: SfxName, opts?: PlayOpts): void {
  const e = eng;
  if (!e || muted) return;
  const spec = SPEC[name];
  if (!spec) return; // unknown name from an untyped caller
  const c = e.c;
  if (c.state !== 'running' && Date.now() > graceUntil) return;

  const now = c.currentTime;
  const prev = lastPlayed.get(name);
  if (prev !== undefined && now - prev < spec.gap / 1000) return;
  voiceEnds = voiceEnds.filter((end) => end > now);
  if (voiceEnds.length >= (spec.prio ? MAX_VOICES : MAX_VOICES - PRIORITY_RESERVE)) return;
  const gain = clamp(num(opts?.gain, 1), 0, 1);
  if (gain === 0) return;

  const pitch = clamp(num(opts?.pitch, 0), -24, 24);
  const cents = spec.vary ? rand(-spec.vary, spec.vary) : 0;
  const ratio = 2 ** ((pitch + cents / 100) / 12);
  // Higher transpositions sound louder to the ear; ease them back a little.
  const loudness = pitch > 0 ? 2 ** (-pitch / 30) : 1;
  const level = gain * loudness * 10 ** ((spec.db ?? 0) / 20);
  lastPlayed.set(name, now);
  try {
    const v = voice(e, now + LOOKAHEAD, ratio, level, clamp(num(opts?.pan, 0), -1, 1), spec.send ?? 0);
    try {
      RECIPES[name](v);
    } finally {
      finish(v);
      voiceEnds.push(v.end);
    }
  } catch {
    // A failed sound must never break the game.
  }
}

function setMuted(m: boolean): void {
  muted = Boolean(m);
  writeStore(MUTED_KEY, muted ? '1' : '0');
  // Millisecond glides: instant to the ear, but never a hard step (a click).
  // Unmuting is faster still so a sound played in the same call is heard fully.
  if (eng) eng.master.gain.setTargetAtTime(masterLevel(), eng.c.currentTime, muted ? 0.005 : 0.002);
}

function setVolume(v: number): void {
  if (typeof v !== 'number' || !Number.isFinite(v)) return;
  volume = clamp(v, 0, 1);
  writeStore(VOLUME_KEY, String(Math.round(volume * 1000) / 1000));
  if (eng) eng.master.gain.setTargetAtTime(masterLevel(), eng.c.currentTime, 0.03);
}

function setTension(t: number): void {
  if (typeof t !== 'number' || !Number.isFinite(t)) return;
  tension = clamp(t, 0, 1);
  const e = eng;
  if (!e) return; // picked up when the drone starts on unlock
  const d = e.drone;
  const delta = tension - d.applied;
  if (delta === 0) return;
  // Callers may push tension every frame; tiny changes wait for the next push.
  if (Math.abs(delta) < 0.01 && e.c.currentTime - d.appliedAt < 0.25) return;
  // Danger arrives quickly; relief fades out slowly.
  applyTension(e, tension, delta > 0 ? 0.6 : 1.6);
}

export const sfx: Sfx = {
  unlock,
  play,
  setMuted,
  get muted() {
    return muted;
  },
  setVolume,
  get volume() {
    return volume;
  },
  setTension,
};
